// URLs da API
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/api/v1/auth/login/',
    logout: '/api/v1/auth/logout/',
    refresh: '/api/v1/auth/refresh/',
    profile: '/api/v1/auth/profile/',
    changePassword: '/api/v1/auth/change-password/',
    loginAttempts: '/api/v1/auth/login-attempts/',
  },
  
  // Core
  core: {
    health: '/api/v1/health/',
    version: '/api/v1/version/',
  },
  
  // Products
  products: {
    base: '/api/v1/products/products/',
    detail: (id: string) => `/api/v1/products/products/${id}/`,
    categories: '/api/v1/products/categories/',
    variants: '/api/v1/products/variants/',
    images: '/api/v1/products/images/',
    bulk: '/api/v1/products/bulk/',
  },
  
  // Shoppub API
  shoppub: {
    health: '/api/v1/shoppub/health/',
    products: '/api/v1/shoppub/products/',
    productDetail: (id: string) => `/api/v1/shoppub/products/${id}/`,
    orders: '/api/v1/shoppub/orders/',
    orderDetail: (id: string) => `/api/v1/shoppub/orders/${id}/`,
    inventory: '/api/v1/shoppub/inventory/',
    sync: '/api/v1/shoppub/sync/',
  },
  
  // Mapping System
  mapping: {
    templates: '/api/v1/mapping/templates/',
    templateDetail: (id: string) => `/api/v1/mapping/templates/${id}/`,
    categories: '/api/v1/mapping/categories/',
    categoryDetail: (id: string) => `/api/v1/mapping/categories/${id}/`,
    products: '/api/v1/mapping/products/',
    productDetail: (id: string) => `/api/v1/mapping/products/${id}/`,
  },
  
  // Sync System
  sync: {
    jobs: '/api/v1/sync/jobs/',
    jobDetail: (id: string) => `/api/v1/sync/jobs/${id}/`,
    conflicts: '/api/v1/sync/conflicts/',
    conflictDetail: (id: string) => `/api/v1/sync/conflicts/${id}/`,
    logs: '/api/v1/sync/logs/',
    trigger: '/api/v1/sync/trigger/',
  },
  
  orders: {
    base: '/api/v1/orders/',
    detail: (id: string) => `/api/v1/orders/${id}/`,
  },
  
  inventory: {
    base: '/api/v1/inventory/',
    movements: '/api/v1/inventory/movements/',
  },
  
  webhooks: {
    base: '/api/v1/webhooks/',
    logs: '/api/v1/webhooks/logs/',
  },
  
  dashboard: {
    metrics: '/api/v1/dashboard/metrics/',
    analytics: '/api/v1/dashboard/analytics/',
  },
};

// Configurações de paginação
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// Configurações de upload
export const UPLOAD = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ACCEPTED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  MAX_IMAGES_PER_PRODUCT: 10,
};

// Status de sincronização
export const SYNC_STATUS = {
  SYNCED: 'synced',
  PENDING: 'pending',
  SYNCING: 'syncing',
  ERROR: 'error',
  CONFLICT: 'conflict',
  DISABLED: 'disabled',
} as const;

export type SyncStatus = typeof SYNC_STATUS[keyof typeof SYNC_STATUS];

// Status de pedidos
export const ORDER_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  PAID: 'paid',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
} as const;

export type OrderStatus = typeof ORDER_STATUS[keyof typeof ORDER_STATUS];

// Tipos de notificação
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const;

export type NotificationType = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];

// Duração de notificações (em ms)
export const NOTIFICATION_DURATION = {
  SHORT: 3000,
  MEDIUM: 5000,
  LONG: 10000,
  PERSISTENT: 0, // Não fecha automaticamente
};

// Configurações de cache
export const CACHE = {
  DEFAULT_TTL: 60 * 1000, // 1 minuto
  PRODUCTS_TTL: 5 * 60 * 1000, // 5 minutos
  CATEGORIES_TTL: 10 * 60 * 1000, // 10 minutos
  USER_TTL: 30 * 60 * 1000, // 30 minutos
};

// Rotas da aplicação
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: (id: string) => `/products/${id}`,
  PRODUCT_CREATE: '/products/new',
  ORDERS: '/orders',
  ORDER_DETAIL: (id: string) => `/orders/${id}`,
  SYNC: '/sync',
  MAPPING: '/mapping',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  ACCESS_DENIED: '/access-denied',
};

// Permissões
export const PERMISSIONS = {
  // Produtos
  PRODUCT_VIEW: 'product.view',
  PRODUCT_CREATE: 'product.create',
  PRODUCT_EDIT: 'product.edit',
  PRODUCT_DELETE: 'product.delete',
  
  // Pedidos
  ORDER_VIEW: 'order.view',
  ORDER_EDIT: 'order.edit',
  ORDER_DELETE: 'order.delete',
  
  // Sincronização
  SYNC_VIEW: 'sync.view',
  SYNC_EXECUTE: 'sync.execute',
  
  // Configurações
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',
  
  // Admin
  ADMIN_ACCESS: 'admin.access',
};

// Configurações de formulário
export const FORM = {
  DEBOUNCE_DELAY: 300, // ms
  MIN_SEARCH_LENGTH: 2,
  MAX_DESCRIPTION_LENGTH: 5000,
  MAX_TITLE_LENGTH: 200,
};